import { useState, useEffect, useCallback } from 'react'
import clsx from 'clsx'
import { styled } from '@mui/material'
import { t } from '$i18n'
import { toEnglishNumber, toLocale } from '$func/text'
import OTPSingleInput from '$common/verification-code-input/OTPSingleInput'

const GAP = 8
const MAX_INPUT_WIDTH = 80

const Input = styled('input')`
  height: 48px;
  width: 100%;
  background-color: ${({ theme }) => theme.palette.neutral000};
  box-sizing: border-box;
  border: ${({ theme }) => `1px solid ${theme.palette.neutral300}`};
  &:focus {
    border: ${({ theme }) => `2px solid ${theme.palette.primary300}`};
  }

  &.hasError {
    border: ${({ theme }) => `1px solid ${theme.palette.error300}`};
    &:focus {
      border: ${({ theme }) => `2px solid ${theme.palette.error300}`};
    }
  }
  outline: none;
  font-size: 20px;
  color: ${({ theme }) => theme.palette.neutral900};
  text-align: center;
  border-radius: 12px;
  -webkit-appearance: initial;
  padding: 8px 12px;
  caret-color: ${({ theme }) => theme.palette.primary500};
  letter-spacing: 30px;
  ::placeholder {
    letter-spacing: 0;
    color: ${({ theme }) => theme.palette.neutral400};
    font-size: ${({ theme }) => theme.typography.h3?.fontSize};
    font-weight: ${({ theme }) => theme.typography.h3?.fontWeight};
  }

  :-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    letter-spacing: 0;
    color: ${({ theme }) => theme.palette.neutral400};
    font-size: ${({ theme }) => theme.typography.h3?.fontSize};
    font-weight: ${({ theme }) => theme.typography.h3?.fontWeight};
  }

  ::-ms-input-placeholder {
    /* Microsoft Edge */
    letter-spacing: 0;
    color: ${({ theme }) => theme.palette.neutral400};
    font-size: ${({ theme }) => theme.typography.h3?.fontSize};
    font-weight: ${({ theme }) => theme.typography.h3?.fontWeight};
  }
`
interface Props {
  className?: string
  numFields: number
  hasError?: boolean
  onChange: (otp: string) => void
  timeout: number
  resendMaxed: boolean
}

let abortController = new AbortController()

export const VerificationCodeInput = styled(
  ({
    className,
    numFields,
    hasError,
    onChange,
    timeout,
    resendMaxed,
  }: Props) => {
    const [otp, setOtp] = useState('')

    const handleChange = useCallback(
      (otpCode: string) => {
        setOtp(toEnglishNumber(otpCode))
        onChange(toEnglishNumber(otpCode))
      },
      [onChange],
    )

    useEffect(() => {
      if ('OTPCredential' in window) {
        if (resendMaxed) {
          abortController.abort(
            'Maximum number of resending otp code exceeded.',
          )
          return
        }
        const input = document.querySelector(
          'input[autocomplete="one-time-code"]',
        )

        if (!input || !navigator.credentials) return
        abortController = new AbortController()
        setTimeout(() => {
          abortController.abort('Timeout exceeded before getting otp from sms.')
        }, timeout)

        navigator.credentials
          .get({
            otp: { transport: ['sms'] },
            signal: abortController.signal,
          })
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .then((credential: any) => {
            // Attention: Cast to any because return type is Credential and Credential does not contain Code
            // https://stackoverflow.com/questions/67182406/web-otp-api-property-code-does-not-exist-on-type-credentialtype
            if (credential?.code) {
              handleChange(credential.code)
            }
          })
          .catch(err => {
            console.error(err)
          })
      }
    }, [timeout, resendMaxed, handleChange])

    return (
      <>
        {/* Do not remove me : This input is for autocomplete when otp is sent */}
        <input
          type="text"
          autoComplete="one-time-code"
          inputMode="numeric"
          style={{
            visibility: 'hidden',
            position: 'absolute',
            pointerEvents: 'none',
          }}
        />
        <OTPSingleInput
          onChange={handleChange}
          value={toLocale(otp)}
          maxLength={numFields}
          renderInput={props => (
            <Input
              {...props}
              className={clsx(className, { hasError })}
              placeholder={t('signin.verification.inputPlaceholder')}
            />
          )}
          shouldAutoFocus
        />
        {/* <OtpInput */}
        {/*  className={className} */}
        {/*  value={toLocale(otp)} */}
        {/*  onChange={handleChange} */}
        {/*  numInputs={numFields} */}
        {/*  renderSeparator={''} */}
        {/*  inputType="tel" */}
        {/*  shouldAutoFocus={true} */}
        {/*  renderInput={props => ( */}
        {/*    <Input */}
        {/*      {...props} */}
        {/*      sx={{ */}
        {/*        width: `calc(${100 / numFields}% - ${(GAP * (numFields - 1)) / */}
        {/*          numFields}px)`, */}
        {/*      }} */}
        {/*      className="otp-input" */}
        {/*      hasError={Boolean(hasError)} */}
        {/*    /> */}
        {/*  )} */}
        {/* /> */}
      </>
    )
  },
)<Props>`
  gap: ${GAP}px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  max-width: ${({ numFields }) =>
    numFields * MAX_INPUT_WIDTH + (numFields - 1) * GAP}px;
`
